import React, {
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchReport, resetReportStatus } from "../../../store/Slices/ReportSlice";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import SearchableTable from "../../../components/Tables/SearchableTable";
import { ExpenseColumns } from "../../../assets/Columns";
import CustomTabs from "../../../components/Tabs/CustomTabs";
import { PaymentReportColumns } from "../../../assets/Columns/PaymentReportColumns";
import ReportInvoiceList from "./ReportInvoiceList";
import ReportInvoiceCardList from "./ReportInvoiceCardList";
import ReportPaymentCardList from "./ReportPaymentCardList";
import ReportExpenseCardList from "./ReportExpenseCardList";
import ReportBuiltiList from "./ReportBuiltiList";
import ReportCustomerSummary from "./ReportCustomerSummary";
import ReportItemSalesSummary from "./ReportItemSalesSummary";
import ReportDateRangeBar, {
  localYmd,
} from "../../../components/Reports/ReportDateRangeBar";
import { isGuestInvoice } from "../../../utils/invoice";
import MetricCard from "../../../components/Cards/MetricCard";
import {
  MdPointOfSale,
  MdMoneyOff,
  MdPayments,
  MdBusiness,
} from "react-icons/md";
import { TbCash } from "react-icons/tb";
import { isAdminRole } from "../../../utils/appPaths";
import {
  buildReportSummary,
  fmtReportRs,
} from "../../../utils/reportSummary";
import { usePageRefresh } from "../../../hooks/usePageRefresh";

const resolveReportBranchId = (user = {}) => {
  const raw = user.branchId ?? user.branch;
  if (!raw) return localStorage.getItem("branch") || "";
  if (typeof raw === "object" && raw._id) return String(raw._id);
  return String(raw);
};

const getBuiltiUnix = (group) => {
  const raw = group?.entry_date ?? group?.date;
  if (raw == null || raw === "") return 0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
};

const TABS = [
  { label: "Customer Invoices" },
  { label: "Summary" },
  { label: "Item Sales" },
  { label: "Guest Invoices" },
  { label: "Returns" },
  { label: "Guest Returns" },
  { label: "Customer Payments" },
  { label: "Company Payments" },
  { label: "Expenses" },
  { label: "Builti Dispatch" },
];

const sumRowsAmount = (rows, keys = ["total_amount", "amount", "expense"]) =>
  (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    if (!row || row.isDeleted === true) return sum;
    for (const key of keys) {
      if (row[key] != null && row[key] !== "") {
        return sum + Number(row[key] || 0);
      }
    }
    return sum;
  }, 0);

const formatTabAmount = (n) =>
  `Rs ${Math.round(Number(n) || 0).toLocaleString("en-PK")}`;

const isCustomerPaymentRow = (row) => {
  if (!row || row.isDeleted === true) return false;
  if (row.user_type === "Customer") return true;
  if (row.user_type === "Company") return false;
  return Boolean(row.customer_name || row.customerId);
};

const isCompanyPaymentRow = (row) => {
  if (!row || row.isDeleted === true) return false;
  if (row.user_type === "Company") return true;
  if (row.user_type === "Customer") return false;
  return Boolean(row.company_name || row.companyId);
};

const DEFAULT_TAB_UI = {
  page: 0,
  rowsPerPage: 100,
  searchText: "",
  searchInvoice: "",
  searchBuilti: "",
  scrollTop: 0,
};

const getReportScrollEl = () =>
  document.querySelector(".scrollable") ||
  document.querySelector('[class*="overflow-y-auto"]');

const Report = () => {
  const isHqAdmin = isAdminRole(); // Super Admin (0) + Admin (1) — same all-branches view
  const branchId = useMemo(() => {
    if (isHqAdmin) return "";
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    return resolveReportBranchId(userData);
  }, [isHqAdmin]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const dispatch = useDispatch();
  const ReportState = useSelector((state) => state.ReportState);
  const [SearchText, setSearchText] = useState("");
  const [SearchInvoice, setSearchInvoice] = useState("");
  const [SearchBuilti, setSearchBuilti] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [SelectedDate, setSelectedDate] = useState(localYmd());
  const [SelectedDateTo, setSelectedDateTo] = useState(localYmd());
  /** Only true after user clicks Apply — no auto backend load on mount/date change. */
  const [hasApplied, setHasApplied] = useState(false);
  const location = useLocation();
  /** Per-tab UI: page / search / scroll — restored when switching tabs. */
  const tabUiStateRef = useRef({});
  const activeTabRef = useRef(0);
  activeTabRef.current = activeTab;
  const skipNextScrollRestoreRef = useRef(true);

  // Keep-alive can restore this page with old hasApplied / shared slice loading flags.
  // Reset before paint so opening /report never flashes a spinner.
  useLayoutEffect(() => {
    const path = location.pathname.replace(/\/+$/, "");
    const onReportPage = path === "/branch/report" || path === "/admin/report";
    if (!onReportPage) return;
    setHasApplied(false);
    dispatch(resetReportStatus());
  }, [location.pathname, dispatch]);

  const reportData = ReportState.data;
  const hasPayload =
    reportData &&
    typeof reportData === "object" &&
    !Array.isArray(reportData);

  const customerTransactions = useMemo(() => {
    if (!hasPayload) return [];
    const base = Array.isArray(reportData.guest_transactions)
      ? reportData.transactions || []
      : (reportData.transactions || []).filter((inv) => !isGuestInvoice(inv));
    // Builti-linked sales show under Builti Dispatch only
    return base.filter((inv) => !Number(inv?.builti_no));
  }, [hasPayload, reportData]);

  const guestTransactions = useMemo(() => {
    if (!hasPayload) return [];
    if (Array.isArray(reportData.guest_transactions)) {
      return reportData.guest_transactions;
    }
    return (reportData.transactions || []).filter(isGuestInvoice);
  }, [hasPayload, reportData]);

  /** All sales invoices for date×item summary (customer + guest, incl. builti-linked). */
  const itemSalesInvoices = useMemo(() => {
    if (!hasPayload) return [];
    if (Array.isArray(reportData.guest_transactions)) {
      return [
        ...(reportData.transactions || []),
        ...reportData.guest_transactions,
      ];
    }
    return reportData.transactions || [];
  }, [hasPayload, reportData]);

  const builtiReport = useMemo(() => {
    if (!reportData || Array.isArray(reportData)) return [];
    const rows = Array.isArray(reportData.builti_report)
      ? [...reportData.builti_report]
      : [];
    return rows.sort((a, b) => {
      const diff = getBuiltiUnix(b) - getBuiltiUnix(a);
      if (diff !== 0) return diff;
      const builtiDiff = Number(b.builti_no || 0) - Number(a.builti_no || 0);
      if (builtiDiff !== 0) return builtiDiff;
      return String(a.company || "").localeCompare(String(b.company || ""));
    });
  }, [reportData]);

  const allReturns = useMemo(() => {
    if (!hasPayload) return [];
    const customer = reportData.returns || [];
    const guest = reportData.guest_returns || [];
    if (guest.length) return [...customer, ...guest];
    return customer;
  }, [hasPayload, reportData]);

  const customerReturns = useMemo(
    () =>
      hasPayload && Array.isArray(reportData.returns)
        ? reportData.returns
        : allReturns.filter((inv) => !isGuestInvoice(inv)),
    [allReturns, hasPayload, reportData]
  );

  const customerNpiInvoices = useMemo(() => {
    if (!hasPayload || !Array.isArray(reportData.non_product_invoices)) return [];
    return reportData.non_product_invoices.filter(
      (row) =>
        row &&
        row.isDeleted !== true &&
        Number(row.user_type) === 1 &&
        (row.customerId?._id || row.customerId)
    );
  }, [hasPayload, reportData]);

  const guestReturns = useMemo(
    () =>
      hasPayload && Array.isArray(reportData.guest_returns)
        ? reportData.guest_returns
        : allReturns.filter((inv) => isGuestInvoice(inv)),
    [allReturns, hasPayload, reportData]
  );

  const guestReturnsTotal = guestReturns.reduce(
    (sum, inv) => sum + Number(inv.total_amount || 0),
    0
  );

  const allPayments = useMemo(
    () => (hasPayload && Array.isArray(reportData.payments) ? reportData.payments : []),
    [hasPayload, reportData]
  );

  const customerPayments = useMemo(
    () => allPayments.filter(isCustomerPaymentRow),
    [allPayments]
  );

  const companyPayments = useMemo(
    () => allPayments.filter(isCompanyPaymentRow),
    [allPayments]
  );

  const handleChangePage = (newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(0);
  };

  const applyReport = useCallback(
    (fromArg, toArg) => {
      // Button onClick passes an event; This Year / Today pass YYYY-MM-DD strings
      const fromOverride = typeof fromArg === "string" ? fromArg : null;
      const toOverride = typeof toArg === "string" ? toArg : null;
      let from = fromOverride || SelectedDate;
      let to = toOverride || SelectedDateTo || SelectedDate;
      if (!from || !to) return;
      if (to < from) {
        const tmp = from;
        from = to;
        to = tmp;
      }
      setSelectedDate(from);
      setSelectedDateTo(to);
      setHasApplied(true);
      dispatch(
        fetchReport({
          branchId,
          startDate: from,
          endDate: to,
          lite: true,
        })
      );
    },
    [dispatch, branchId, SelectedDate, SelectedDateTo]
  );

  const refreshReport = useCallback(() => {
    if (!hasApplied) return;
    dispatch(
      fetchReport({
        branchId,
        startDate: SelectedDate,
        endDate: SelectedDateTo || SelectedDate,
        lite: true,
      })
    );
  }, [dispatch, branchId, SelectedDate, SelectedDateTo, hasApplied]);

  usePageRefresh(refreshReport, [branchId, SelectedDate, SelectedDateTo, hasApplied]);

  const summary = useMemo(
    () => buildReportSummary(reportData),
    [reportData]
  );

  const dateLabel =
    SelectedDate === SelectedDateTo
      ? SelectedDate
      : `${SelectedDate} → ${SelectedDateTo}`;

  const saveActiveTabUi = useCallback(() => {
    const tab = activeTabRef.current;
    const el = getReportScrollEl();
    tabUiStateRef.current[tab] = {
      page,
      rowsPerPage,
      searchText: SearchText,
      searchInvoice: SearchInvoice,
      searchBuilti: SearchBuilti,
      scrollTop: el?.scrollTop ?? 0,
    };
  }, [page, rowsPerPage, SearchText, SearchInvoice, SearchBuilti]);

  const handleTabChange = useCallback(
    (nextTab) => {
      if (nextTab === activeTabRef.current) return;
      saveActiveTabUi();
      const saved = tabUiStateRef.current[nextTab] || { ...DEFAULT_TAB_UI };
      setPage(saved.page ?? 0);
      setRowsPerPage(saved.rowsPerPage ?? 100);
      setSearchText(saved.searchText ?? "");
      setSearchInvoice(saved.searchInvoice ?? "");
      setSearchBuilti(saved.searchBuilti ?? "");
      skipNextScrollRestoreRef.current = false;
      setActiveTab(nextTab);
    },
    [saveActiveTabUi]
  );

  // Restore this tab's scroll after its content mounts
  useLayoutEffect(() => {
    if (skipNextScrollRestoreRef.current) {
      skipNextScrollRestoreRef.current = false;
      return;
    }
    const saved = tabUiStateRef.current[activeTab];
    const el = getReportScrollEl();
    if (!el) return;
    const top = saved?.scrollTop ?? 0;
    el.scrollTop = top;
    // Content height can settle one frame later (lists/cards)
    requestAnimationFrame(() => {
      const node = getReportScrollEl();
      if (node) node.scrollTop = top;
    });
  }, [activeTab]);

  const guestTotal = guestTransactions.reduce(
    (sum, inv) => sum + Number(inv.total_amount || 0),
    0
  );

  const tabsWithAmounts = useMemo(() => {
    const customerInvTotal = sumRowsAmount(customerTransactions, [
      "total_amount",
    ]);
    const guestInvTotal = sumRowsAmount(guestTransactions, ["total_amount"]);
    const customerReturnTotal = sumRowsAmount(customerReturns, [
      "total_amount",
    ]);
    const guestReturnTotal = sumRowsAmount(guestReturns, ["total_amount"]);
    const customerPayTotal = sumRowsAmount(customerPayments, ["amount"]);
    const companyPayTotal = sumRowsAmount(companyPayments, ["amount"]);
    const expenseTotal = sumRowsAmount(
      hasPayload ? reportData.expenses || [] : [],
      ["expense", "amount"]
    );
    const builtiTotal = sumRowsAmount(builtiReport, [
      "total_amount",
      "dispatch_amount",
      "amount",
    ]);

    let npiCredit = 0;
    let npiDebit = 0;
    for (const row of customerNpiInvoices) {
      const amt = Number(row.amount || 0);
      if (Number(row.invoice_type) === 1) npiCredit += amt;
      else npiDebit += amt;
    }
    // Summary tab = sales − returns + NPI credit − NPI debit
    const summaryTotal =
      customerInvTotal - customerReturnTotal + npiCredit - npiDebit;

    const itemSalesTotal = itemSalesInvoices.reduce((sum, inv) => {
      if (!inv || inv.isDeleted === true) return sum;
      const items = Array.isArray(inv.items) ? inv.items : [];
      if (items.length) {
        return (
          sum +
          items.reduce((s, item) => s + Number(item.amount || 0), 0)
        );
      }
      return sum + Number(inv.total_amount || 0);
    }, 0);

    const amounts = [
      customerInvTotal,
      summaryTotal,
      itemSalesTotal,
      guestInvTotal,
      customerReturnTotal,
      guestReturnTotal,
      customerPayTotal,
      companyPayTotal,
      expenseTotal,
      builtiTotal,
    ];

    return TABS.map((tab, index) => ({
      ...tab,
      amount: amounts[index] || 0,
      amountLabel: formatTabAmount(amounts[index] || 0),
    }));
  }, [
    customerTransactions,
    guestTransactions,
    itemSalesInvoices,
    customerReturns,
    guestReturns,
    customerPayments,
    companyPayments,
    customerNpiInvoices,
    builtiReport,
    hasPayload,
    reportData,
  ]);

  return (
    <BodyWrapper>
      <Header
        title="Daily Report"
        desc="Sales, guest, returns, payments, expenses & builti stock"
      />

      <div className="mb-3">
        <ReportDateRangeBar
          fromDate={SelectedDate}
          toDate={SelectedDateTo}
          onFromChange={setSelectedDate}
          onToChange={setSelectedDateTo}
          onRangeChange={(from, to) => {
            setSelectedDate(from);
            setSelectedDateTo(to);
          }}
          onApply={applyReport}
          showToday={false}
          refreshing={
            hasApplied &&
            Boolean(ReportState.loading || ReportState.refreshing)
          }
          periodLabel={
            isHqAdmin ? `${dateLabel} · All branches` : dateLabel
          }
        />
        {hasApplied && ReportState.isError ? (
          <p className="mt-2 text-xs font-semibold text-danger">
            Report load failed. Date range dobara Apply karein.
          </p>
        ) : null}
      </div>

      <div className="w-full">
        {!hasApplied ? (
          <div className="rounded-xl border border-line bg-card px-4 py-12 text-center text-inkSecondary text-sm font-quicksand">
            Date range select karein, phir{" "}
            <span className="font-bold text-main">Apply</span> dabayein — tab
            backend se report load hogi.
          </div>
        ) : hasApplied && ReportState.loading && !ReportState.data ? (
          <div className="flex justify-center items-center py-16">
            <FetchingLoading />
          </div>
        ) : hasApplied ? (
          <div
            className={`flex flex-col w-full gap-4 transition-opacity duration-200 ${
              ReportState.refreshing ? "opacity-60" : "opacity-100"
            }`}
          >
            {isHqAdmin && (
              <section className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                  <MetricCard
                    compact
                    title="Total Sales"
                    value={fmtReportRs(summary.total_sales)}
                    subtitle={`${summary.invoice_count} invoice${summary.invoice_count !== 1 ? "s" : ""}`}
                    tone="brand"
                    icon={MdPointOfSale}
                  />
                  <MetricCard
                    compact
                    title="Guest Sales"
                    value={fmtReportRs(summary.guest_sales)}
                    subtitle={`${summary.guest_invoice_count} guest invoice${summary.guest_invoice_count !== 1 ? "s" : ""}`}
                    tone="warning"
                    icon={TbCash}
                  />
                  <MetricCard
                    compact
                    title="Expenses"
                    value={fmtReportRs(summary.total_expenses)}
                    subtitle={`${summary.expense_count} expense${summary.expense_count !== 1 ? "s" : ""}`}
                    tone="danger"
                    icon={MdMoneyOff}
                  />
                  <MetricCard
                    compact
                    title="Cash Received"
                    value={fmtReportRs(summary.cash_received_from_customers)}
                    subtitle={`${summary.customer_cash_payment_count ?? 0} customer payment${(summary.customer_cash_payment_count ?? 0) !== 1 ? "s" : ""}`}
                    tone="success"
                    icon={MdPayments}
                  />
                  <MetricCard
                    compact
                    title="Paid to Companies"
                    value={fmtReportRs(summary.cash_paid_to_companies)}
                    subtitle={`${summary.company_cash_payment_count ?? 0} company payment${(summary.company_cash_payment_count ?? 0) !== 1 ? "s" : ""}`}
                    tone="danger"
                    icon={MdBusiness}
                  />
                  <MetricCard
                    compact
                    title="Net Cash"
                    value={fmtReportRs(summary.net_cash_movement)}
                    subtitle="Cash in − company paid − expenses"
                    tone={summary.net_cash_movement >= 0 ? "success" : "danger"}
                    icon={TbCash}
                  />
                </div>
              </section>
            )}

            <div className="sticky top-0 z-30 -mx-1 px-1 py-2 bg-page border-b border-line/60 max850:top-14">
              <CustomTabs
                tabs={tabsWithAmounts}
                activeTab={activeTab}
                setActiveTab={handleTabChange}
                scrollable
              />
            </div>

            {activeTab === 0 &&
              (isHqAdmin ? (
                <ReportInvoiceCardList
                  invoices={customerTransactions}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  searchText={SearchText}
                  setSearchText={setSearchText}
                  searchInvoice={SearchInvoice}
                  setSearchInvoice={setSearchInvoice}
                  namePlaceholder="Search customer"
                  emptyMessage="No customer invoices found for this date range."
                  editable
                />
              ) : (
                <ReportInvoiceList
                  invoices={customerTransactions}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  searchText={SearchText}
                  setSearchText={setSearchText}
                  searchInvoice={SearchInvoice}
                  setSearchInvoice={setSearchInvoice}
                  namePlaceholder="Search customer"
                  emptyMessage="No customer invoices found for this date range."
                />
              ))}

            {activeTab === 1 && (
              <ReportCustomerSummary
                salesInvoices={customerTransactions}
                returnInvoices={customerReturns}
                npiInvoices={customerNpiInvoices}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                searchText={SearchText}
                setSearchText={setSearchText}
              />
            )}

            {activeTab === 2 && (
              <ReportItemSalesSummary
                salesInvoices={itemSalesInvoices}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                searchText={SearchText}
                setSearchText={setSearchText}
              />
            )}

            {activeTab === 3 && (
              <>
                {guestTransactions.length > 0 && (
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/20 bg-warningSoft px-4 py-3">
                    <p className="text-sm font-semibold text-warning">
                      Guest / counter sales — cash on spot (no customer ledger)
                    </p>
                    <p className="text-sm tabular-nums font-bold text-warning">
                      Total:{" "}
                      {guestTotal.toLocaleString("en-PK", {
                        style: "currency",
                        currency: "PKR",
                        maximumFractionDigits: 0,
                      })}
                    </p>
                  </div>
                )}
                {isHqAdmin ? (
                  <ReportInvoiceCardList
                    invoices={guestTransactions}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    searchText={SearchText}
                    setSearchText={setSearchText}
                    searchInvoice={SearchInvoice}
                    setSearchInvoice={setSearchInvoice}
                    namePlaceholder="Search guest name"
                    emptyMessage="No guest invoices found for this date range."
                    variant="guest"
                  />
                ) : (
                  <ReportInvoiceList
                    invoices={guestTransactions}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    searchText={SearchText}
                    setSearchText={setSearchText}
                    searchInvoice={SearchInvoice}
                    setSearchInvoice={setSearchInvoice}
                    namePlaceholder="Search guest name"
                    emptyMessage="No guest invoices found for this date range."
                  />
                )}
              </>
            )}

            {activeTab === 4 &&
              (isHqAdmin ? (
                <ReportInvoiceCardList
                  invoices={customerReturns}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  searchText={SearchText}
                  setSearchText={setSearchText}
                  searchInvoice={SearchInvoice}
                  setSearchInvoice={setSearchInvoice}
                  namePlaceholder="Search customer"
                  emptyMessage="No customer returns found for this date range."
                  variant="return"
                />
              ) : (
                <ReportInvoiceList
                  invoices={customerReturns}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  searchText={SearchText}
                  setSearchText={setSearchText}
                  searchInvoice={SearchInvoice}
                  setSearchInvoice={setSearchInvoice}
                  namePlaceholder="Search customer"
                  emptyMessage="No customer returns found for this date range."
                />
              ))}

            {activeTab === 5 && (
              <>
                {guestReturns.length > 0 && (
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger/20 bg-dangerSoft px-4 py-3">
                    <p className="text-sm font-semibold text-danger">
                      Guest returns — cash refunded to walk-in / guest
                    </p>
                    <p className="text-sm tabular-nums font-bold text-danger">
                      Total:{" "}
                      {guestReturnsTotal.toLocaleString("en-PK", {
                        style: "currency",
                        currency: "PKR",
                        maximumFractionDigits: 0,
                      })}
                    </p>
                  </div>
                )}
                {isHqAdmin ? (
                  <ReportInvoiceCardList
                    invoices={guestReturns}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    searchText={SearchText}
                    setSearchText={setSearchText}
                    searchInvoice={SearchInvoice}
                    setSearchInvoice={setSearchInvoice}
                    namePlaceholder="Search guest name"
                    emptyMessage="No guest returns found for this date range."
                    variant="guest-return"
                  />
                ) : (
                  <ReportInvoiceList
                    invoices={guestReturns}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    searchText={SearchText}
                    setSearchText={setSearchText}
                    searchInvoice={SearchInvoice}
                    setSearchInvoice={setSearchInvoice}
                    namePlaceholder="Search guest name"
                    emptyMessage="No guest returns found for this date range."
                    editable={false}
                  />
                )}
              </>
            )}

            {activeTab === 6 &&
              (isHqAdmin ? (
                <ReportPaymentCardList
                  payments={customerPayments}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  searchText={SearchText}
                  setSearchText={setSearchText}
                  emptyMessage="No customer payments found for this date range."
                />
              ) : (
                <>
                  {customerPayments.length > 0 && (
                    <SearchableTable
                      SearchPlaceholder="Search invoice #…"
                      SearchText={SearchText}
                      setSearchText={setSearchText}
                      CurrentData={customerPayments}
                      Columns={PaymentReportColumns}
                    />
                  )}
                  {customerPayments.length === 0 && (
                    <div className="w-full text-center text-muted py-10 text-lg">
                      No customer payments found for this date range.
                    </div>
                  )}
                </>
              ))}

            {activeTab === 7 &&
              (isHqAdmin ? (
                <ReportPaymentCardList
                  payments={companyPayments}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  searchText={SearchText}
                  setSearchText={setSearchText}
                  emptyMessage="No company payments found for this date range."
                />
              ) : (
                <>
                  {companyPayments.length > 0 && (
                    <SearchableTable
                      SearchPlaceholder="Search invoice #…"
                      SearchText={SearchText}
                      setSearchText={setSearchText}
                      CurrentData={companyPayments}
                      Columns={PaymentReportColumns}
                    />
                  )}
                  {companyPayments.length === 0 && (
                    <div className="w-full text-center text-muted py-10 text-lg">
                      No company payments found for this date range.
                    </div>
                  )}
                </>
              ))}

            {activeTab === 8 &&
              (isHqAdmin ? (
                <ReportExpenseCardList
                  expenses={hasPayload ? reportData.expenses || [] : []}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  searchText={SearchText}
                  setSearchText={setSearchText}
                  emptyMessage="No expenses found for this date range."
                />
              ) : (
                <>
                  {hasPayload && reportData.expenses?.length > 0 && (
                    <SearchableTable
                      SearchPlaceholder="Search description…"
                      SearchText={SearchText}
                      setSearchText={setSearchText}
                      CurrentData={reportData.expenses}
                      Columns={ExpenseColumns.filter((col) => col.id !== "actions")}
                    />
                  )}
                  {hasPayload &&
                    (!reportData.expenses || reportData.expenses.length === 0) && (
                      <div className="w-full text-center text-muted py-10 text-lg">
                        No expenses found for this date range.
                      </div>
                    )}
                </>
              ))}

            {activeTab === 9 && (
              <>
                {builtiReport.length > 0 && (
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-main/20 bg-brandSoft px-4 py-3">
                    <p className="text-sm font-semibold text-main">
                      Builti Dispatch — stock received & customer dispatch (separate from Customer Invoices)
                      invoice #
                    </p>
                    <p className="text-sm tabular-nums font-bold text-main">
                      {builtiReport.length} builti
                      {builtiReport.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                )}
                <ReportBuiltiList
                  builtiGroups={builtiReport}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  searchText={SearchText}
                  setSearchText={setSearchText}
                  searchBuilti={SearchBuilti}
                  setSearchBuilti={setSearchBuilti}
                />
              </>
            )}
          </div>
        ) : null}
      </div>
    </BodyWrapper>
  );
};

export default Report;
